import React, { useEffect } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { ENVIRONMENT, DEFAULT_COUNTRY_CODE } from '../../../utils/constants';
import PropTypes from 'prop-types';

/**
 * We have multiple Convert projects configured here
 * https://app.convert.com/accounts/1004961
 * Determining corresponding Convert script for each project.
 */
const convertProjectId =
  ENVIRONMENT !== 'production'
    ? '10043192'
    : DEFAULT_COUNTRY_CODE === 'IE'
      ? '10041496'
      : '10043106';

export const ConvertStyle = () => (
  <Head>
    <style
      dangerouslySetInnerHTML={{
        __html: `
.behaviour-settings__ab-variant--variant-b,
.behaviour-settings__ab-variant--variant-c {
  display: none;
}
.ab-variant--variant-b .behaviour-settings__ab-variant--variant-b {
  display: revert;
}
.ab-variant--variant-b .behaviour-settings__ab-variant--variant-a,
.ab-variant--variant-b .behaviour-settings__ab-variant--variant-c {
  display: none;
}
.ab-variant--variant-c .behaviour-settings__ab-variant--variant-c {
  display: revert;
}
.ab-variant--variant-c .behaviour-settings__ab-variant--variant-a,
.ab-variant--variant-c .behaviour-settings__ab-variant--variant-b {
  display: none;
}
`,
      }}
    />
  </Head>
);

/**
 * Injects Convert script and some setup code before it.
 *
 * Note that we are NOT using synchronous script as it's recommended by Convert.
 * In initial tests synchronous script significantly damaged performance and
 * didn't play well with Next.js.
 */
const Convert = ({ nonce }) => {
  useEffect(() => {
    window._conv_q = window._conv_q || [];
    window._conv_q.push({ what: 'consentRequired', params: { runExperiences: false } });

    const handleCookiebotConsent = () => {
      if (window.Cookiebot?.consent?.statistics) {
        window._conv_q = window._conv_q || [];
        window._conv_q.push({ what: 'consentGiven' });
      }
    };

    if (window.Cookiebot?.consent?.statistics) {
      window._conv_q.push({ what: 'consentGiven' });
    }

    window.addEventListener('CookiebotOnAccept', handleCookiebotConsent);
    window.addEventListener('CookiebotOnDecline', handleCookiebotConsent);

    return () => {
      window.removeEventListener('CookiebotOnAccept', handleCookiebotConsent);
      window.removeEventListener('CookiebotOnDecline', handleCookiebotConsent);
    };
  }, []);

  return (
    <>
      <Script id="convert-setup" strategy="beforeInteractive" nonce={nonce}>
        {`
          function cwConvertSetVariant(variant = 'A') {
            if (document.body) {
              document.body.classList.add('ab-variant--variant-' + variant.toLowerCase());
            }
          }
          function cwConvertSetVariantInWindow(variant = 'A') {
            if (typeof window !== 'undefined') {
              window.cwConvertVariant = variant;
            }
          }
        `}
      </Script>
      <Script
        id="convert-script"
        src={`//cdn-4.convertexperiments.com/v1/js/1004961-${convertProjectId}.js?environment=production`}
        strategy="beforeInteractive"
        nonce={nonce}
      />
    </>
  );
};

Convert.propTypes = {
  nonce: PropTypes.string.isRequired,
};

export default Convert;
